/**
 * Produces inference inputs from the live <video>:
 *  - an ImageBitmap (transferable to the worker), optionally downscaled so the
 *    inference cost is bounded regardless of the camera resolution;
 *  - an occasional mean-luminance measurement (16x16 sample) for the
 *    "too dark" guidance.
 *
 * `createImageBitmap(video, {resize...})` is the fast path (GPU copy, no
 * readback). Where it is missing or throws for video sources (some Safari
 * versions), we fall back to drawing into a canvas.
 */
export interface SceneLight {
  /** Mean luminance of the face region, 0..255. */
  faceLuma: number;
  /** Mean colour of the frame, normalised so that the average channel is 1 (colour cast). */
  tint: { r: number; g: number; b: number };
  /**
   * Where the light comes from, estimated from the face's shading: -1..1 left
   * to right (image coordinates, un-mirrored) and -1..1 bottom to top.
   */
  dirX: number;
  dirY: number;
}

export class FrameGrabber {
  private readonly video: HTMLVideoElement;
  private fallbackCanvas: OffscreenCanvas | HTMLCanvasElement | null = null;
  private fallbackCtx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null = null;
  private bitmapFromVideoBroken = false;
  private readonly lumaCanvas: HTMLCanvasElement;
  private readonly lumaCtx: CanvasRenderingContext2D | null;
  private readonly lightCanvas: HTMLCanvasElement;
  private readonly lightCtx: CanvasRenderingContext2D | null;
  private lastLuma: number | null = null;
  private lumaCounter = 0;
  /** Long side of the bitmap handed to the tracker. */
  targetLongSide = 960;

  constructor(video: HTMLVideoElement) {
    this.video = video;
    this.lumaCanvas = document.createElement('canvas');
    this.lumaCanvas.width = 16;
    this.lumaCanvas.height = 16;
    this.lumaCtx = this.lumaCanvas.getContext('2d', { willReadFrequently: true });
    this.lightCanvas = document.createElement('canvas');
    this.lightCanvas.width = 32;
    this.lightCanvas.height = 32;
    this.lightCtx = this.lightCanvas.getContext('2d', { willReadFrequently: true });
  }

  /** Target size for the inference bitmap, preserving aspect ratio. */
  targetSize(): { width: number; height: number } {
    const vw = this.video.videoWidth;
    const vh = this.video.videoHeight;
    const long = Math.max(vw, vh);
    if (long <= this.targetLongSide) return { width: vw, height: vh };
    const s = this.targetLongSide / long;
    return { width: Math.round(vw * s), height: Math.round(vh * s) };
  }

  async grab(source: HTMLVideoElement | HTMLCanvasElement = this.video): Promise<ImageBitmap | null> {
    if (this.video.readyState < 2 || this.video.videoWidth === 0) return null;
    const { width, height } = this.targetSize();

    if (!this.bitmapFromVideoBroken && typeof createImageBitmap === 'function') {
      try {
        // A110Y: 'medium' — 'low' is a plain bilinear sample, aliased at ×2.5 (1280 → 512): noisier landmarks at the low levels.
        return await createImageBitmap(source, { resizeWidth: width, resizeHeight: height, resizeQuality: 'medium' });
      } catch {
        this.bitmapFromVideoBroken = true;
      }
    }

    // Fallback: draw into a canvas, then convert.
    if (!this.fallbackCanvas || this.fallbackCanvas.width !== width || this.fallbackCanvas.height !== height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this.fallbackCanvas = new OffscreenCanvas(width, height);
        this.fallbackCtx = this.fallbackCanvas.getContext('2d') as OffscreenCanvasRenderingContext2D | null;
      } else {
        const c = document.createElement('canvas');
        c.width = width;
        c.height = height;
        this.fallbackCanvas = c;
        this.fallbackCtx = c.getContext('2d');
      }
    }
    if (!this.fallbackCtx) return null;
    this.fallbackCtx.drawImage(source, 0, 0, width, height);
    if (this.fallbackCanvas instanceof OffscreenCanvas) return this.fallbackCanvas.transferToImageBitmap();
    return createImageBitmap(this.fallbackCanvas);
  }

  /**
   * Scene light estimate for the renderer: mean luminance of the face region
   * (exposure) and mean chromaticity of the whole frame (colour cast), from a
   * 32x32 readback. `faceBounds` is normalised (0..1) or null (whole frame).
   */
  sceneLight(faceBounds: { x: number; y: number; w: number; h: number } | null): SceneLight | null {
    if (!this.lightCtx || this.video.videoWidth === 0) return null;
    try {
      this.lightCtx.drawImage(this.video, 0, 0, 32, 32);
      const d = this.lightCtx.getImageData(0, 0, 32, 32).data;
      let r = 0;
      let g = 0;
      let b = 0;
      let faceLuma = 0;
      let faceN = 0;
      let lumaL = 0;
      let lumaR = 0;
      let lumaT = 0;
      let lumaB = 0;
      let nL = 0;
      let nR = 0;
      let nT = 0;
      let nB = 0;
      const fx0 = faceBounds ? Math.floor(faceBounds.x * 32) : 0;
      const fy0 = faceBounds ? Math.floor(faceBounds.y * 32) : 0;
      const fx1 = faceBounds ? Math.ceil((faceBounds.x + faceBounds.w) * 32) : 32;
      const fy1 = faceBounds ? Math.ceil((faceBounds.y + faceBounds.h) * 32) : 32;
      for (let y = 0; y < 32; y++) {
        for (let x = 0; x < 32; x++) {
          const i = (y * 32 + x) * 4;
          const pr = d[i]!;
          const pg = d[i + 1]!;
          const pb = d[i + 2]!;
          r += pr;
          g += pg;
          b += pb;
          if (x >= fx0 && x < fx1 && y >= fy0 && y < fy1) {
            const l = 0.299 * pr + 0.587 * pg + 0.114 * pb;
            faceLuma += l;
            faceN++;
            const mx = (fx0 + fx1) / 2;
            const my = (fy0 + fy1) / 2;
            if (x < mx) {
              lumaL += l;
              nL++;
            } else {
              lumaR += l;
              nR++;
            }
            if (y < my) {
              lumaT += l;
              nT++;
            } else {
              lumaB += l;
              nB++;
            }
          }
        }
      }
      const n = 32 * 32;
      const mean = (r + g + b) / (3 * n) || 1;
      const mL = nL ? lumaL / nL : 0;
      const mR = nR ? lumaR / nR : 0;
      const mT = nT ? lumaT / nT : 0;
      const mB = nB ? lumaB / nB : 0;
      const dirX = mL + mR > 1 ? (mR - mL) / (mR + mL) : 0;
      const dirY = mT + mB > 1 ? (mT - mB) / (mT + mB) : 0;
      return {
        faceLuma: faceN > 0 ? faceLuma / faceN : (0.299 * r + 0.587 * g + 0.114 * b) / n,
        tint: { r: r / n / mean, g: g / n / mean, b: b / n / mean },
        dirX: Math.max(-1, Math.min(1, dirX * 3)),
        dirY: Math.max(-1, Math.min(1, dirY * 3)),
      };
    } catch {
      return null;
    }
  }

  /** Mean luminance 0..255, refreshed every `every` calls (cheap 16x16 readback). */
  luma(every = 15): number | null {
    if (!this.lumaCtx || this.video.videoWidth === 0) return this.lastLuma;
    if (this.lumaCounter++ % every !== 0) return this.lastLuma;
    try {
      this.lumaCtx.drawImage(this.video, 0, 0, 16, 16);
      const d = this.lumaCtx.getImageData(0, 0, 16, 16).data;
      let sum = 0;
      for (let i = 0; i < d.length; i += 4) sum += 0.299 * d[i]! + 0.587 * d[i + 1]! + 0.114 * d[i + 2]!;
      this.lastLuma = sum / (d.length / 4);
    } catch {
      // Tainted canvas or similar: keep the last value.
    }
    return this.lastLuma;
  }
}
